/**
 * 10 - Free the Hostages (03 §2): a concrete prison with a reinforced door and 2-4 hostages,
 * a guard house beside it with 3-5 guards and the officer holding the detonator. Alert 3
 * before the door is open starts a 40 s demolition; once it is open a squad of rocket
 * troopers comes in from the perimeter for the hostages.
 *
 * Both buildings are on the strip map (ROUND5 §2.4): the prison is what the mission asks for, and the
 * guard house is the only answer to the countdown the orders offer.
 */

import { NO_STAND } from '../builder';
import { FLAG } from '../grid';
import { LIMITS } from '../tuning';
import {
  checker, clutter, countKind, guard, guardAtDoor, insideRect, required, ruleOf, structure, targetsOf,
} from './helpers';
import { flagTargets } from './helpers3b';
import type { ArchetypeSpec } from './types';

const FIRST_CAPTIVE_ID = 700;

export const hostages: ArchetypeSpec = {
  id: 'hostages',
  name: 'FREE THE HOSTAGES',
  nativeBiome: 'desert',
  baseThreat: 6,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wall',
  sealed: false,
  layout: 'compound',
  size: { w: [46, 56], d: [28, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const prison = required(g, 'prison', { target: true, margin: 2.2 });
    const at = [{ x: prison.x, z: prison.z }];
    // "Adjacent": close enough that the guards hear the door go.
    const house = structure(g, 'guardHouse', { near: prison, maxDist: 13, minDist: 7, from: at, margin: 1.2 })
      ?? required(g, 'guardHouse', { minDist: 7, from: at, margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);

    const count = 2 + g.rng.int(3);
    for (let i = 0; i < count; i++) {
      const x = prison.x - (count - 1) * 0.6 + i * 1.2;
      g.map.captives.push({ id: FIRST_CAPTIVE_ID + i, kind: 'hostage', x, z: prison.z });
    }
    guardAtDoor(g, house, 'officer');
    const guards = 3 + g.rng.int(3);
    for (let i = 0; i < guards; i++) {
      guard(g, house.x + g.rng.range(-6, 6), house.z + g.rng.range(-5, 6), g.rng.range(-1, 1));
    }
    guardAtDoor(g, prison);

    // The executioners wait at the corners of the perimeter until the door is open.
    const squad = g.threat >= LIMITS.specialsFrom ? 3 + g.rng.int(2) : 2;
    const r = g.rect;
    const corners: Array<[number, number]> = [
      [r.x0 + 3.5, r.z0 + 3.5], [r.x1 - 3.5, r.z0 + 3.5], [r.x1 - 3.5, r.z1 - 3.5], [r.x0 + 3.5, r.z1 - 3.5],
    ];
    const first = g.rng.int(4);
    for (let i = 0; i < squad; i++) {
      const corner = corners[(first + i) % 4];
      const spot = corner ? g.map.nearestOpen(corner[0] + (i >= 4 ? 2 : 0), corner[1], 4, NO_STAND | FLAG.keep) : null;
      if (!spot) g.map.problems.push('hostages: nowhere for the rocket troopers to come from');
      else g.map.spawn({ kind: 'rocketTrooper', x: spot[0], z: spot[1], facing: 0, dormantUntil: 'objectiveOpened' });
    }
    return {
      objective: {
        kind: 'freeAndEscort', label: 'FREE HOSTAGES', targetPropIds: [prison.id], minFraction: 1,
        rules: [
          { type: 'alertConsequence', level: 3, delaySeconds: 40, effect: 'demolition' },
          { type: 'recall', mode: 'normal' },
        ],
      },
      // ROUND5 §2.4 (the marker audit): the orders answer the 40-second demolition with "KILL THE OFFICER
      // HOLDING THE DETONATOR IN THE GUARD HOUSE", and the guard house was nowhere on the map. It is a mark,
      // not a target: what the mission asks for is still the prison. Flagged here rather than at build time
      // because a flag then posts another sentry (posts.ts) on a building that already has the whole detail.
      finish(def) {
        flagTargets(def, [prison.id, house.id]);
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('hostages');
    const prison = targetsOf(def, 'prison')[0];
    check(prison !== undefined && def.objective.targetPropIds.length === 1, 'the prison must be the one target');
    check(def.objective.kind === 'freeAndEscort', 'objective must be freeAndEscort');
    check(
      def.captives.length >= 2 && def.captives.length <= 4 && def.captives.every((c) => c.kind === 'hostage'),
      'needs 2-4 hostages',
    );
    if (prison) {
      for (const c of def.captives) {
        check(
          Math.abs(c.x - prison.x) <= 3.2 && Math.abs(c.z - prison.z) <= 2.6,
          `hostage ${c.id} is not in the prison`,
        );
      }
    }
    check(countKind(def, 'guardHouse') >= 1, 'needs the guard house');
    check(prison?.target === true, 'the prison must be marked on the strip map');
    // ROUND5: the guard house is the mission's answer to the demolition count, so it is on the strip map
    // too - and the officer who holds the detonator really stands at the one that is marked.
    const marked = def.props.filter((p) => p.kind === 'guardHouse' && p.target === true);
    check(marked.length === 1, `the guard house with the detonator must be marked on the strip map (${marked.length} marked)`);
    const officers = def.spawns.filter((s) => s.kind === 'officer' && insideRect(def.goalZone, s.x, s.z));
    check(officers.length >= 1, 'needs the officer with the detonator');
    const [house] = marked;
    if (house) {
      check(
        officers.some((s) => Math.abs(s.x - house.x) <= 9 && Math.abs(s.z - house.z) <= 9),
        'the officer with the detonator must stand at the marked guard house',
      );
    }
    const squad = def.spawns.filter((s) => s.kind === 'rocketTrooper' && s.dormantUntil === 'objectiveOpened');
    check(squad.length >= 2, 'needs a rocket squad that appears once the door is open');
    const wired = ruleOf(def, 'alertConsequence');
    check(
      wired?.effect === 'demolition' && wired.level === 3 && wired.delaySeconds === 40,
      'alert 3 must start the 40 s demolition',
    );
    return out;
  },
};
